import { useCallback, useMemo, useRef, useState } from 'react';

import type { ModelProduct } from '@app/api/Products';
import type {
  AddProductsModalProps,
  ProductTreeLeaf,
  SupplierId,
} from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/types';
import { groupProductsByCategory } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/utils';
import type { RangeInterface } from '@app/shared/components/Range/types';

import type { TreeDataNode, TreeProps } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import type { ChangeEventHandler } from 'react';

export const useAddProductsModal = ({
  isOpen,
  onClose,
  productList,
  suppliersOptions,
  onAdd,
  selectedProducts,
}: AddProductsModalProps) => {
  const idToKeyRef = useRef<Map<number, React.Key>>(new Map());
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<BaseOptionType[]>(
    []
  );
  const [range, setRange] = useState<RangeInterface>({
    from: undefined,
    to: undefined,
  });
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  const onSelect: TreeProps['onSelect'] = (selectedKeys, { node }) => {
    if (!node.isLeaf) return;
    const key = node.key as React.Key;
    setCheckedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const onCheck: TreeProps['onCheck'] = (chkKeys) =>
    setCheckedKeys(chkKeys as React.Key[]);

  const getTreeData = useCallback((): TreeDataNode[] => {
    idToKeyRef.current.clear();
    const supplierSet = new Set<SupplierId | null>(
      selectedSuppliers.map((o) => o.value)
    );
    const query = searchValue.trim().toLowerCase();

    const filtered: ModelProduct[] = productList.filter((p) => {
      if (p.IsDeleted) {
        return false;
      }

      if (supplierSet.size && !supplierSet.has(p.SupplierId ?? null)) {
        return false;
      }

      const { from, to } = range;

      if (
        (typeof from === 'number' && Number(p?.Amount) <= from) ||
        (typeof to === 'number' && Number(p?.Amount) >= to)
      )
        return false;

      if (query) {
        const haystack = [p.Name, p.NameShort, p.Article, p.BarCode]
          .filter((f): f is string => typeof f === 'string')
          .map((s) => s.toLowerCase())
          .join(' ');

        if (!haystack.includes(query)) return false;
      }

      return true;
    });

    let groups = groupProductsByCategory(filtered);

    if (query) {
      groups = groups.filter((g) => {
        const titleHit =
          typeof g?.title === 'string' && g.title.toLowerCase().includes(query);
        const hasKids = (g.children?.length ?? 0) > 0;
        return titleHit || hasKids;
      });
    }

    groups.forEach((cat) => {
      (cat.children ?? []).forEach((leaf) => {
        const product = (leaf as ProductTreeLeaf).dataRef;
        const unit = product.UnitOfMeasurement;

        (leaf as ProductTreeLeaf).title = unit
          ? `${product.Name}, ${unit}`
          : product.Name;

        idToKeyRef.current.set(product.Id, leaf.key);
      });
    });

    return groups;
  }, [productList, selectedSuppliers, searchValue, range]);

  const treeData = useMemo(getTreeData, [getTreeData]);

  const checkedKeysHandler = useCallback(() => {
    if (isOpen) {
      const keys = selectedProducts
        .map((p) => idToKeyRef.current.get(p.Id))
        .filter((k): k is React.Key => k !== undefined);

      setCheckedKeys(keys);
    } else {
      setCheckedKeys([]);
    }
  }, [selectedProducts, isOpen]);

  const onSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchValue(e.target.value);
  };

  const skipFilters = useCallback(() => {
    setSearchValue('');
    setSelectedSuppliers([]);
    setRange({ from: undefined, to: undefined });
  }, []);

  const productMap = useMemo<Map<React.Key, ModelProduct>>(() => {
    const map = new Map<React.Key, ModelProduct>();

    treeData.forEach((cat) => {
      (cat.children ?? []).forEach((leaf) => {
        map.set(leaf.key, (leaf as ProductTreeLeaf).dataRef);
      });
    });

    return map;
  }, [treeData]);

  const addProducts = useCallback(() => {
    const selected: ModelProduct[] = checkedKeys
      .map((k) => productMap.get(k))
      .filter((p): p is ModelProduct => p !== undefined);

    onAdd(selected);
  }, [checkedKeys, onAdd, productMap]);

  const onCancel = useCallback(() => {
    setCheckedKeys([]);
    skipFilters();
    onClose();
  }, [onClose, skipFilters]);

  return {
    checkedKeys,
    onSelect,
    onCheck,
    isOpen,
    suppliersOptions,
    treeData,
    selectedSuppliers,
    setSelectedSuppliers,
    searchValue,
    onSearch,
    setRange,
    range,
    skipFilters,
    addProducts,
    onCancel,
    checkedKeysHandler,
  };
};
